import type { MarkupConfig } from '../types';

export const DEFAULT_PRODUCTION_BRANCHES = ['main', 'master'];

type SiteConfig = Pick<
  Partial<MarkupConfig>,
  'branch' | 'productionBranches' | 'devHosts'
>;

/**
 * True when the widget is on a production site and must stay off the page
 * entirely.
 *
 * 1. If the build's git branch is known (passed in, or detected on Vercel),
 *    that decides: production branches hide the widget, anything else shows it.
 * 2. Otherwise the page's domain decides: the widget shows only on places
 *    that are recognisably not live (see `isDevHost`), and hides everywhere
 *    else — an unrecognised domain is assumed to be a client's live site.
 */
export function isProductionSite(config?: SiteConfig): boolean {
  const branch = normalise(config?.branch ?? detectBranch());
  if (branch) {
    const production = config?.productionBranches ?? DEFAULT_PRODUCTION_BRANCHES;
    return production.some((b) => normalise(b) === branch);
  }
  // No window (SSR): nothing renders there anyway.
  if (typeof window === 'undefined') return false;
  return !isDevHost(window.location.hostname, config?.devHosts);
}

/** Preview/tunnel platforms: every host under these is a non-live deploy. */
const DEV_DOMAINS = [
  'localhost',
  'local',
  'test',
  'vercel.app',
  'netlify.app',
  'pages.dev',
  'ngrok.io',
  'ngrok.app',
  'ngrok-free.app',
];

/**
 * Our own staging domains: every subdomain is a client preview. The bare
 * domain isn't included — that's a live site.
 */
const DEV_PARENT_DOMAINS = ['sirendigital.uk'];

/** Words that mark a subdomain as non-live: staging.x.com, client-dev.x.com. */
const DEV_WORDS = new Set([
  'staging', 'stage', 'stg', 'dev', 'develop', 'development',
  'preview', 'uat', 'test', 'testing', 'qa', 'sandbox',
]);

/**
 * Whether `hostname` is somewhere the widget belongs: a local machine or IP,
 * a preview platform, a subdomain named like staging (`staging.client.com`,
 * `client-uat.agency.com`), or a host listed in `devHosts` (exact, or
 * `*.example.com` for it and every subdomain).
 *
 * Only subdomains are checked for the words, never the domain itself, so a
 * live site called `best-test-kitchen.com` still counts as live.
 */
export function isDevHost(hostname: string, devHosts: string[] = []): boolean {
  const host = hostname.toLowerCase().replace(/\.$/, '');
  if (!host) return true; // file:// and friends — not a public site
  if (/^[\d.]+$/.test(host) || host.includes(':')) return true; // IPv4 / IPv6

  const under = (domain: string) => host === domain || host.endsWith('.' + domain);

  for (const pattern of devHosts) {
    const p = pattern.toLowerCase().trim();
    if (p.startsWith('*.') ? under(p.slice(2)) : host === p) return true;
  }
  if (DEV_DOMAINS.some(under)) return true;
  if (DEV_PARENT_DOMAINS.some((d) => host.endsWith('.' + d))) return true;

  const subdomains = host.split('.').slice(0, -2);
  return subdomains.some((label) =>
    label.split('-').some((word) => DEV_WORDS.has(word.replace(/\d+$/, ''))),
  );
}

function normalise(ref: string | undefined): string {
  return (ref ?? '').trim().replace(/^refs\/heads\//, '');
}

/**
 * The branch the host's bundler baked in, when we can find one without the
 * host doing anything: Next.js on Vercel exposes
 * NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF and inlines it wherever it appears — in
 * this package too — so it must be written out in full, not destructured.
 * Anywhere else `process` may not exist, hence the try.
 */
function detectBranch(): string | undefined {
  try {
    return process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF;
  } catch (_) {
    return undefined;
  }
}
